import React, { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import './LoginForm.css'

function LoginForm() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [localError, setLocalError] = useState('')

  const { login, error, clearError } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Get the page user was trying to access
  const from = location.state?.from?.pathname || '/'

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear errors when user starts typing
    if (localError) setLocalError('')
    if (error) clearError()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setLocalError('')

    // Basic validation
    if (!formData.email || !formData.password) {
      setLocalError('Please fill in all fields')
      setIsLoading(false)
      return
    }

    try {
      const result = await login(formData.email, formData.password)
      
      if (result.success) {
        // Redirect to the page they were trying to access
        navigate(from, { replace: true })
      } else if (result.nextStep) {
        // Handle different challenge types
        if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          // Redirect to new password form
          navigate('/new-password', { state: { from: location.state?.from } })
        } else {
          setLocalError(`Additional step required: ${result.nextStep.signInStep}`)
        }
      } else {
        setLocalError(result.error || result.message || 'Login failed')
      }
    } catch {
      setLocalError('An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  const displayError = localError || error

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Sign In</h1>
          <p>Welcome back to Wingbeat Paragliding</p>
          <div className="login-help">
            <small>
              <strong>New members:</strong> Use your username (not email) and the temporary password from your invitation email. Username and password are case-sensitive.
            </small>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {displayError && (
            <div className="error-message">
              {displayError}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Username or Email</label>
            <input
              type="text"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Username or email address"
              required
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
          </div>

          <button 
            type="submit" 
            className="auth-button primary"
            disabled={isLoading}
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="auth-links">
          <Link to="/forgot-password" className="link">
            Forgot your password?
          </Link>
          <div className="auth-divider">
            <span>Need an account? Contact a club administrator</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginForm